import sqlite3InitModule, {} from "@sqlite.org/sqlite-wasm";
export const CLIENT_DATABASE_FILENAME = "/client-node.sqlite3";
export const CLIENT_SAH_POOL_DIRECTORY = "/node-client-sqlite-sahpool";
export const CLIENT_SAH_POOL_NAME = "node-client-opfs-sahpool";
export class OpfsUnavailableError extends Error {
    constructor() {
        super("Persistent browser storage is unavailable");
        this.name = "OpfsUnavailableError";
    }
}
export class StorageQuotaExceededError extends Error {
    constructor() {
        super("Persistent browser storage quota is exhausted");
        this.name = "StorageQuotaExceededError";
    }
}
export class StorageCorruptError extends Error {
    constructor() {
        super("Persistent browser storage is corrupt");
        this.name = "StorageCorruptError";
    }
}
export class StorageWriterConflictError extends Error {
    constructor() {
        super("Another client-node storage writer is active");
        this.name = "StorageWriterConflictError";
    }
}
export async function openInMemorySqliteRuntimeForTests() {
    const sqlite3 = await sqlite3InitModule();
    const database = new sqlite3.oo1.DB(":memory:", "c");
    return runtime(sqlite3, database, async () => undefined);
}
export async function openOpfsSqliteRuntime(options) {
    return await new OpfsSahPoolRuntimeOwner(options).open();
}
export class OpfsSahPoolRuntimeOwner {
    #directory;
    #filename;
    #loadModule;
    #poolName;
    #poolPromise;
    constructor(options) {
        this.#directory = options.directory ?? CLIENT_SAH_POOL_DIRECTORY;
        this.#filename = options.filename;
        this.#loadModule = options.loadModule ?? sqlite3InitModule;
        this.#poolName = options.poolName ?? CLIENT_SAH_POOL_NAME;
    }
    async open() {
        let pool;
        try {
            const initialized = await this.#pool();
            const activePool = initialized.pool;
            pool = activePool;
            const { sqlite3 } = initialized;
            if (activePool.isPaused())
                await activePool.unpauseVfs();
            const database = new activePool.OpfsSAHPoolDb(this.#filename);
            return runtime(sqlite3, database, async () => await activePool.wipeFiles(), () => { activePool.pauseVfs(); });
        }
        catch (error) {
            try {
                pool?.pauseVfs();
            }
            catch {
                throw new OpfsUnavailableError();
            }
            throw classifyPersistentStorageError(error);
        }
    }
    async wipe() {
        try {
            const { pool } = await this.#pool();
            if (pool.isPaused())
                await pool.unpauseVfs();
            try {
                await pool.wipeFiles();
            }
            finally {
                pool.pauseVfs();
            }
        }
        catch (error) {
            throw classifyPersistentStorageError(error);
        }
    }
    async #pool() {
        let poolPromise = this.#poolPromise;
        if (poolPromise === undefined) {
            poolPromise = this.#initializePool();
            this.#poolPromise = poolPromise;
            void poolPromise.catch(() => {
                if (this.#poolPromise === poolPromise)
                    this.#poolPromise = undefined;
            });
        }
        return await poolPromise;
    }
    async #initializePool() {
        const sqlite3 = await this.#loadModule();
        if (typeof sqlite3.installOpfsSAHPoolVfs !== "function")
            throw new OpfsUnavailableError();
        if (typeof sqlite3.capi.sqlite3_js_db_export !== "function")
            throw new OpfsUnavailableError();
        const pool = await sqlite3.installOpfsSAHPoolVfs({
            clearOnInit: false,
            directory: this.#directory,
            initialCapacity: 6,
            name: this.#poolName,
        });
        return { pool, sqlite3 };
    }
}
function runtime(sqlite3, database, wipe, pause = () => undefined) {
    let databaseClosed = false;
    let vfsPaused = false;
    return {
        close: () => {
            if (!databaseClosed) {
                database.close();
                databaseClosed = true;
            }
            if (!vfsPaused) {
                pause();
                vfsPaused = true;
            }
        },
        database,
        exportDatabase: () => sqlite3.capi.sqlite3_js_db_export(database),
        wipe,
    };
}
export function classifyPersistentStorageError(error, fallback = "opfs-unavailable") {
    if (error instanceof OpfsUnavailableError ||
        error instanceof StorageCorruptError ||
        error instanceof StorageQuotaExceededError ||
        error instanceof StorageWriterConflictError)
        return error;
    const name = error instanceof DOMException ? error.name : "";
    const message = error instanceof Error ? error.message : String(error);
    if (name === "QuotaExceededError" || /quota|SQLITE_FULL/iu.test(message)) {
        return new StorageQuotaExceededError();
    }
    if (/SQLITE_(?:CORRUPT|NOTADB)|corrupt|malformed|not a database/iu.test(message)) {
        return new StorageCorruptError();
    }
    if (/SQLITE_(?:BUSY|LOCKED)|\b(?:lock|locked|locking)\b|already.*(?:open|active)|access handle/iu.test(message)) {
        return new StorageWriterConflictError();
    }
    if (fallback === "corrupt")
        return new StorageCorruptError();
    if (fallback === "opfs-unavailable")
        return new OpfsUnavailableError();
    return error instanceof Error ? error : new Error("Persistent storage operation failed");
}
//# sourceMappingURL=sqlite-runtime.js.map